import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { useQuery } from "@apollo/client/react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import useAuth from "../../../hooks/useAuth";
import useQueryParams from "../../../hooks/useQueryParams";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import SelectReceivingModal from "../../../views/favoriteReport/SelectReceivingModal";
import ReportHistoryModal from "./ReportHistoryModal";
import BillingReportViewerModal from "./BillingReportViewerModal";
import EmailBillingReportModal from "./EmailBillingReportModal";
import { BILLING_PERIODS_QUERY, FAVORITE_REPORTS_QUERY } from "../../../constants/graphql/queries";
import {
  DELETE_BILLING_REPORT_MUTATION,
  EMAIL_BILLING_REPORT_MUTATION,
  SEND_REPLACEMENT_INVOICE_MUTATION,
  GENERATE_INVOICE_EXPORT_MUTATION,
} from "../../../constants/graphql/mutations";
import moment from "moment";

export default function Billing() {
  const { user } = useAuth();
  const isBillingUser = user?.role === "billing_user";
  const { notifySuccess, notifyError } = useNotification();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generatingModalOpen, setGeneratingModalOpen] = useState(false);
  const [generatingIframeUrl, setGeneratingIframeUrl] = useState(null);
  const [billingReportToDelete, setBillingReportToDelete] = useState(null);
  const [replacementReport, setReplacementReport] = useState(null);
  const [emailBillingReport] = useMutation(EMAIL_BILLING_REPORT_MUTATION);
  const [deleteBillingReport] = useMutation(DELETE_BILLING_REPORT_MUTATION);
  const [sendReplacementInvoice] = useMutation(SEND_REPLACEMENT_INVOICE_MUTATION);
  const [generateInvoiceExport] = useMutation(GENERATE_INVOICE_EXPORT_MUTATION);

  const historyModalOpen = queryParams.get("historyModalOpen") === "true";
  const selectedPeriodId = queryParams.get("periodId");
  const emailBillingModalOpen = queryParams.get("emailBillingModalOpen") === "true";
  const emailBillingReportId = queryParams.get("billingReportId");
  const billingReportViewerOpen = queryParams.get("billingReportViewerOpen") === "true";
  const selectedReportUrl = queryParams.get("billingReportUrl");
  const selectedReportName = queryParams.get("billingReportName") || "Billing Report";

  const { data, loading, error, refetch } = useQuery(BILLING_PERIODS_QUERY, {
    variables: { projectId, limit: 24 },
    skip: !projectId,
  });

  const { data: favoriteReportsData } = useQuery(FAVORITE_REPORTS_QUERY);

  const billingReportTemplate = useMemo(() => {
    const reports = favoriteReportsData?.favoriteReports || [];
    return reports.find(
      report => report.payload?.template_name === "favorite_report_billing_template.xlsx"
    );
  }, [favoriteReportsData]);

  const allPeriods = data?.project?.billingPeriods || [];
  const project = data?.project;

  const selectedPeriod = useMemo(() => {
    if (!selectedPeriodId) return null;
    return allPeriods.find(p => p.id === selectedPeriodId) || null;
  }, [allPeriods, selectedPeriodId]);

  const emailModalReport = useMemo(() => {
    if (!emailBillingModalOpen || !emailBillingReportId || !selectedPeriod?.billingReports) {
      return null;
    }
    return selectedPeriod.billingReports.find(r => r.id === emailBillingReportId) || null;
  }, [emailBillingModalOpen, emailBillingReportId, selectedPeriod]);

  useEffect(() => {
    if (historyModalOpen && selectedPeriodId) {
      refetch();
    }
  }, [historyModalOpen, selectedPeriodId, refetch]);

  useEffect(() => {
    if (emailBillingModalOpen && emailBillingReportId) {
      refetch();
    }
  }, [emailBillingModalOpen, emailBillingReportId, refetch]);

  useEffect(() => {
    if (loading || !emailBillingModalOpen || !emailBillingReportId || emailModalReport) return;
    const params = new URLSearchParams(location.search);
    params.delete("emailBillingModalOpen");
    params.delete("billingReportId");
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [
    loading,
    emailBillingModalOpen,
    emailBillingReportId,
    emailModalReport,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Filter to show:
  // 1. Periods that have a report (hasReport === true)
  // 2. The current period (today is between startDate and endDate)
  // 3. The previous period (the most recent period that has ended)
  const billingPeriods = useMemo(() => {
    const today = moment();

    // Find the current period
    const currentPeriodIndex = allPeriods.findIndex(period => {
      const startDate = moment(period.startDate);
      const endDate = moment(period.endDate);
      return today.isSameOrAfter(startDate, "day") && today.isSameOrBefore(endDate, "day");
    });

    // Find the previous period (most recent period that has ended)
    const previousPeriod = allPeriods.find(period => {
      const endDate = moment(period.endDate);
      return today.isAfter(endDate, "day");
    });

    return allPeriods.filter(period => {
      // Show if period has a report
      if (period.hasReport) {
        return true;
      }

      // Show if this is the current period
      const startDate = moment(period.startDate);
      const endDate = moment(period.endDate);
      const isCurrentPeriod =
        today.isSameOrAfter(startDate, "day") && today.isSameOrBefore(endDate, "day");

      if (isCurrentPeriod) {
        return true;
      }

      // Show if this is the previous period
      if (previousPeriod && period.id === previousPeriod.id) {
        return true;
      }

      return false;
    });
  }, [allPeriods]);

  const handleOpenHistoryModal = period => {
    const params = new URLSearchParams(location.search);
    params.set("historyModalOpen", "true");
    params.set("periodId", period.id);
    navigate({ search: params.toString() });
  };

  const handleCloseHistoryModal = () => {
    const params = new URLSearchParams(location.search);
    params.delete("historyModalOpen");
    params.delete("periodId");
    params.delete("emailBillingModalOpen");
    params.delete("billingReportId");
    navigate({ search: params.toString() });
  };

  const openReportViewer = useCallback((reportUrl, fileName) => {
    if (!reportUrl) return;

    const params = new URLSearchParams(location.search);
    params.set("billingReportViewerOpen", "true");
    params.set("billingReportUrl", reportUrl);

    if (fileName) {
      params.set("billingReportName", fileName);
    } else {
      params.delete("billingReportName");
    }

    navigate({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [location.pathname, location.search, navigate]);

  const handleCloseReportViewer = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("billingReportViewerOpen");
    params.delete("billingReportUrl");
    params.delete("billingReportName");

    navigate({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [location.pathname, location.search, navigate]);

  const handleViewReport = report => {
    const defaultLabel = selectedPeriod?.label ? `${selectedPeriod.label} Billing Report` : "Billing Report";
    const versionLabel = report?.version ? ` - Version ${report.version}` : "";
    openReportViewer(report?.reportUrl, `${defaultLabel}${versionLabel}`);
  };

  const handleGenerateReportSubmit = values => {
    if (!billingReportTemplate) {
      setSnackbarMessage("No billing report template found for this project");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const startDate = moment(values.startDate).format("MM/DD/YYYY");
    const endDate = moment(values.endDate).format("MM/DD/YYYY");

    const url = `/billing_reports/check_billing_report_link?project_id=${projectId}&start_date=${startDate}&end_date=${endDate}&token=${user?.token}`;

    setGeneratingIframeUrl(url);
    setGeneratingModalOpen(true);
    setGenerateModalOpen(false);
  };

  const handleOpenEmailModal = report => {
    const params = new URLSearchParams(location.search);
    params.set("emailBillingModalOpen", "true");
    params.set("billingReportId", report.id);
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  const handleCloseEmailModal = () => {
    const params = new URLSearchParams(location.search);
    params.delete("emailBillingModalOpen");
    params.delete("billingReportId");
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  const handleSendBillingReportEmail = async variables => {
    await emailBillingReport({
      variables,
      onSuccess: async () => {
        notifySuccess("Billing report email queued");
        handleCloseEmailModal();
        await refetch();
      },
      onFailure: errors => {
        notifyError(errors?.submit || Object.values(errors || {})[0] || "Failed to queue billing report email");
      },
    });
  };

  const handleConfirmDeleteBillingReport = async () => {
    if (!billingReportToDelete?.id) return;

    await deleteBillingReport({
      variables: { billingReportId: billingReportToDelete.id },
      onSuccess: async () => {
        notifySuccess(`Deleted billing report version ${billingReportToDelete.version}`);
        setBillingReportToDelete(null);
        await refetch();
      },
      onFailure: errors => {
        setBillingReportToDelete(null);
        notifyError(errors?.submit || Object.values(errors || {})[0] || "Failed to delete billing report");
      },
    });
  };

  const handleDeleteBillingReport = report => {
    setBillingReportToDelete(report);
  };

  const handleConfirmReplacementInvoice = async () => {
    if (!replacementReport?.id) return;

    await sendReplacementInvoice({
      variables: { billingReportId: replacementReport.id },
      onSuccess: async response => {
        const invoiceNumber = response?.invoice?.number;
        const deliveryDisabled = response?.invoice?.exports?.every(
          invoiceExport => invoiceExport.status === "ready"
        );
        notifySuccess(
          deliveryDisabled
            ? `Replacement invoice ${invoiceNumber || ""} prepared; FTP delivery is disabled`
            : `Replacement invoice ${invoiceNumber || ""} queued for delivery`
        );
        setReplacementReport(null);
        await refetch();
      },
      onFailure: errors => {
        setReplacementReport(null);
        notifyError(
          errors?.submit ||
            Object.values(errors || {})[0] ||
            "Failed to generate replacement invoice"
        );
      },
    });
  };

  const handleGenerateInvoiceExport = async report => {
    if (!report?.id) return;

    await generateInvoiceExport({
      variables: { billingReportId: report.id },
      onSuccess: async response => {
        const blob = new Blob([response?.content || ""], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = response?.export?.filename || `${response?.invoice?.number || "invoice"}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        notifySuccess(`Invoice ${response?.invoice?.number || ""} downloaded`);
        await refetch();
      },
      onFailure: errors => {
        notifyError(errors?.submit || Object.values(errors || {})[0] || "Failed to generate invoice file");
      },
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Generate the polling page URL for iframe
  const getPollingPageUrl = useCallback(() => {
    if (!selectedPeriod || !user?.token) return null;
    const startDate = moment(selectedPeriod.startDate).format("MM/DD/YYYY");
    const endDate = moment(selectedPeriod.endDate).format("MM/DD/YYYY");
    return `/billing_reports/view?project_id=${projectId}&start_date=${startDate}&end_date=${endDate}&token=${user?.token}`;
  }, [selectedPeriod, user?.token, projectId]);

  // Handle "Latest Version" button click - open iframe modal
  const handleLatestVersionClick = useCallback(() => {
    const url = getPollingPageUrl();
    if (url) {
      setGeneratingIframeUrl(url);
      setGeneratingModalOpen(true);
    }
  }, [getPollingPageUrl]);

  // Handle closing the generating modal
  const handleCloseGeneratingModal = useCallback(() => {
    setGeneratingModalOpen(false);
    setGeneratingIframeUrl(null);
  }, []);

  // Listen for postMessage from iframe when report is ready
  useEffect(() => {
    const handleMessage = event => {
      if (event.data?.type === "BILLING_REPORT_READY" && event.data?.reportUrl) {
        // Close the generating modal
        setGeneratingModalOpen(false);
        setGeneratingIframeUrl(null);
        // Navigate to the full-page viewer with the generated report
        const fileName = selectedPeriod?.label
          ? `${selectedPeriod.label} Billing Report`
          : "Billing Report";
        openReportViewer(event.data.reportUrl, fileName);
        // Refresh the data to show the new report in the list
        refetch();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [openReportViewer, selectedPeriod, refetch]);

  return (
    <Paper>
      <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center">
          <Typography fontWeight={500} mr={1}>
            Billing Reports
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setGenerateModalOpen(true)}
          disabled={!billingReportTemplate}
          title={
            !billingReportTemplate ? "No billing report template configured for this project" : ""
          }
        >
          Generate New Billing Report
        </Button>
      </Box>

      <Box p={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Billing Periods</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell align="center">
                    <Alert
                      severity="error"
                      action={
                        <Button color="inherit" size="small" onClick={() => refetch()}>
                          Retry
                        </Button>
                      }
                    >
                      Billing reports could not be loaded. Try again or contact support.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : billingPeriods.length === 0 ? (
                <TableRow>
                  <TableCell align="center">
                    <Typography color="text.secondary">No billing periods available</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                billingPeriods.map(period => {
                  const versionCount = period.billingReports?.length || 0;
                  return (
                    <TableRow
                      key={period.id}
                      hover
                      onClick={() => handleOpenHistoryModal(period)}
                      selected={selectedPeriod?.id === period.id}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography fontWeight={500}>{period.label}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {moment(period.startDate).format("MM/DD/YY")} -{" "}
                              {moment(period.endDate).format("MM/DD/YY")}
                            </Typography>
                          </Box>
                          {versionCount > 0 && (
                            <Typography variant="body2" color="text.secondary">
                              {versionCount} {versionCount === 1 ? "version" : "versions"}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {billingPeriods.length > 0 && (
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Showing {billingPeriods.length} billing period
              {billingPeriods.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        )}
      </Box>

      <ReportHistoryModal
        open={historyModalOpen}
        onClose={handleCloseHistoryModal}
        period={selectedPeriod}
        projectId={projectId}
        onLatestVersionClick={handleLatestVersionClick}
        onViewReport={handleViewReport}
        onEmailReport={handleOpenEmailModal}
        onSendReplacement={setReplacementReport}
        onGenerateInvoiceExport={handleGenerateInvoiceExport}
        onDeleteReport={handleDeleteBillingReport}
        canDeleteReport={!isBillingUser}
      />

      <BillingReportViewerModal
        open={billingReportViewerOpen}
        onClose={handleCloseReportViewer}
        fileUrl={selectedReportUrl}
        fileName={selectedReportName}
      />

      <EmailBillingReportModal
        open={Boolean(emailModalReport)}
        report={emailModalReport}
        projectName={project?.name || selectedPeriod?.label || "Billing Report"}
        projectBillingEmailSendList={project?.billingEmailSendList || ""}
        companyEmails={(project?.client?.company?.emailsSendList || "").split(/[,;]+/).map(email => email.trim()).filter(Boolean)}
        clientBillingEmails={(project?.client?.clientBillingEmailSendList || "").split(/[,;]+/).map(email => email.trim()).filter(Boolean)}
        onClose={handleCloseEmailModal}
        onSend={handleSendBillingReportEmail}
        onViewReport={handleViewReport}
        allowEditProjectRecipients={!isBillingUser}
      />

      {billingReportToDelete && (
        <ConfirmDeleteModal
          title="Delete Billing Report"
          descr={`Are you sure you want to delete billing report version ${billingReportToDelete.version}?`}
          onClose={() => setBillingReportToDelete(null)}
          onDelete={handleConfirmDeleteBillingReport}
        />
      )}

      <Dialog
        open={Boolean(replacementReport)}
        onClose={() => setReplacementReport(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Replacement Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            Generate a new invoice number from billing report version{" "}
            {replacementReport?.version}? The prior invoice remains immutable and should be
            deleted from Moveware before the replacement is posted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplacementReport(null)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmReplacementInvoice} variant="contained">
            Generate Replacement
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Generating Modal with Embedded Polling Page */}
      <Dialog
        open={generatingModalOpen}
        onClose={handleCloseGeneratingModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { minHeight: "500px" },
        }}
      >
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          {generatingIframeUrl && (
            <iframe
              src={generatingIframeUrl}
              title="Generating Billing Report"
              style={{
                width: "100%",
                height: "450px",
                border: "none",
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGeneratingModal} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {generateModalOpen && billingReportTemplate && (
        <SelectReceivingModal
          selectedProjectId={projectId}
          name={billingReportTemplate.payload?.name || "Billing Report"}
          title="Generate Report"
          description={`Template: ${billingReportTemplate.payload?.name || "Billing Report"}`}
          buttonLabel="Generate Report"
          onSubmit={handleGenerateReportSubmit}
          onClose={() => setGenerateModalOpen(false)}
          alwaysShowDates={true}
          showReceivingNotes={false}
          reportType={billingReportTemplate.payload?.report_type}
          isPredefined={billingReportTemplate.payload?.predefined}
        />
      )}
    </Paper>
  );
}
